import React, { useState } from "react";
import { useI18n } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { sendContact } from "@/api";
import { toast } from "sonner";
import { MessageCircle, Mail, Phone } from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";

const WHATSAPP_NUMBER = "393470011246";

export default function ContactSection() {
  const { lang, t } = useI18n();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [consent, setConsent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return;
    if (!consent) {
      toast.error(lang === "it" ? "Devi accettare la Privacy" : "You must accept the Privacy Policy");
      return;
    }
    try {
      setLoading(true);
      await sendContact({ ...form, language: lang });
      toast.success(t.contact.sent);
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error("Error");
    } finally {
      setLoading(false);
    }
  };

  const waText = encodeURIComponent(
    lang === "it"
      ? "Ciao! Vorrei informazioni sui vostri servizi di autista privato in Gallura."
      : "Hi! I'd like info on your private driver services in Gallura.",
  );

  return (
    <section id="contact" data-testid="contact-section" className="py-24 lg:py-32 bg-[#F9F7F3]">
      <div className="max-w-6xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-12">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] accent-text">{t.contact.eyebrow}</p>
          <h2 className="font-display text-4xl md:text-5xl brand-text mt-4 leading-tight">
            {t.contact.title}
          </h2>
          <p className="text-base md:text-lg text-slate-600 mt-5">{t.contact.lead}</p>

          <div className="mt-10 space-y-4">
            <a
              href={`https://wa.me/${WHATSAPP_NUMBER}?text=${waText}`}
              target="_blank"
              rel="noreferrer"
              data-testid="whatsapp-cta"
              className="flex items-center gap-3 p-4 rounded-xl bg-white border border-slate-200 hover:border-[#0A2463] transition-colors"
            >
              <div className="w-11 h-11 rounded-full bg-[#25D366] grid place-items-center text-white">
                <MessageCircle className="w-5 h-5" />
              </div>
              <div>
                <div className="font-medium brand-text">{t.contact.whatsapp}</div>
                <div className="text-xs text-slate-500">+39 347 001 1246</div>
              </div>
            </a>
            <a
              href="mailto:info@galluradrive.it"
              data-testid="email-link"
              className="flex items-center gap-3 p-4 rounded-xl bg-white border border-slate-200"
            >
              <div className="w-11 h-11 rounded-full brand-bg grid place-items-center text-white">
                <Mail className="w-5 h-5" />
              </div>
              <div>
                <div className="font-medium brand-text">Email</div>
                <div className="text-xs text-slate-500">info@galluradrive.it</div>
              </div>
            </a>
            <a
              href="tel:+393470011246"
              data-testid="phone-link"
              className="flex items-center gap-3 p-4 rounded-xl bg-white border border-slate-200"
            >
              <div className="w-11 h-11 rounded-full accent-bg grid place-items-center text-white">
                <Phone className="w-5 h-5" />
              </div>
              <div>
                <div className="font-medium brand-text">{t.contact.phone}</div>
                <div className="text-xs text-slate-500">+39 347 001 1246</div>
              </div>
            </a>
          </div>

          {/* Site URL QR Code */}
          <div
            data-testid="site-qr-card"
            className="mt-6 p-5 rounded-xl bg-white border border-slate-200 flex items-center gap-5"
          >
            <div className="p-2 bg-white border border-slate-200 rounded-lg shrink-0">
              <QRCodeCanvas
                value="https://galluradrive.it"
                size={110}
                fgColor="#0A2463"
                bgColor="#FFFFFF"
                level="H"
                includeMargin={false}
              />
            </div>
            <div>
              <div className="font-medium brand-text">{t.contact.qr_title}</div>
              <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                {t.contact.qr_hint}
              </p>
            </div>
          </div>
        </div>

        <form
          onSubmit={submit}
          data-testid="contact-form"
          className="bg-white rounded-2xl p-7 md:p-10 border border-slate-200 shadow-[0_8px_30px_rgba(0,0,0,0.04)] space-y-4"
        >
          <div>
            <Label>{t.contact.name}</Label>
            <Input
              data-testid="contact-name-input"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
            />
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>{t.contact.email}</Label>
              <Input
                type="email"
                data-testid="contact-email-input"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
              />
            </div>
            <div>
              <Label>{t.contact.phone}</Label>
              <Input
                data-testid="contact-phone-input"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
              />
            </div>
          </div>
          <div>
            <Label>{t.contact.message}</Label>
            <Textarea
              rows={5}
              data-testid="contact-message-input"
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              required
            />
          </div>
          <Button
            type="submit"
            data-testid="contact-submit-btn"
            disabled={loading}
            className="brand-bg text-white rounded-full px-6 w-full"
          >
            {t.contact.send}
          </Button>
          <label className="flex items-start gap-2 text-xs text-slate-600 cursor-pointer">
            <input
              type="checkbox"
              data-testid="contact-consent-checkbox"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              className="mt-0.5"
            />
            <span>
              {lang === "it" ? "Accetto l'" : "I accept the "}
              <a href="/privacy" target="_blank" rel="noreferrer" className="underline brand-text">
                {lang === "it" ? "Informativa Privacy" : "Privacy Policy"}
              </a>.
            </span>
          </label>
        </form>
      </div>

      {/* Floating WhatsApp */}
      <a
        href={`https://wa.me/${WHATSAPP_NUMBER}?text=${waText}`}
        target="_blank"
        rel="noreferrer"
        data-testid="floating-whatsapp"
        className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-[#25D366] grid place-items-center text-white shadow-2xl hover:scale-105 transition-transform"
        aria-label="WhatsApp"
      >
        <MessageCircle className="w-6 h-6" />
      </a>
    </section>
  );
}
